import type { DataState } from '../lib/dataState';
import { DATA_STATE_LABEL, DATA_STATE_MARK, renderDataState } from '../lib/dataState';

/**
 * Renderiza um valor com o ESTADO dele visivel.
 *
 * Tres exigencias da spec materializadas aqui:
 * - ausente, obsoleto, estimado e indisponivel sao visualmente DISTINTOS;
 * - ausente nunca aparece como zero;
 * - o significado NAO depende so de cor: cada estado tem marcador textual e
 *   texto acessivel proprio (WCAG 2.2 AA, Back Office §20).
 */

const STATE_STYLE: Record<DataState['kind'], string> = {
  available: 'text-ink',
  // Estimado: tracejado + marcador `≈`. Nunca rotulado como observado.
  estimated: 'text-ink border-b border-dashed border-line-strong',
  unavailable: 'text-ink-faint italic',
  not_configured: 'text-ink-muted',
  // Obsoleto: fundo marcado E marcador `!`. Nunca "verde saudavel".
  stale: 'text-ink bg-surface-sunken px-1 rounded',
  insufficient_sample: 'text-ink-faint italic',
  not_enough_classified_audience: 'text-ink-muted',
  phase_pending: 'text-ink-faint',
};

export function DataValue({ state, label }: { state: DataState; label: string }) {
  const mark = DATA_STATE_MARK[state.kind];
  const stateLabel = DATA_STATE_LABEL[state.kind];

  return (
    <span className="inline-flex items-baseline gap-1">
      <span className={STATE_STYLE[state.kind]} data-state={state.kind}>
        {renderDataState(state)}
      </span>
      {/* Texto so para leitor de tela: repete o estado em palavras. */}
      <span className="sr-only">{`${label}: ${stateLabel}`}</span>
      {mark !== '' && (
        <abbr
          title={explain(state)}
          aria-hidden="true"
          className="text-xs text-ink-faint no-underline"
        >
          {mark}
        </abbr>
      )}
    </span>
  );
}

/** Explicacao em linguagem simples do POR QUE o valor esta nesse estado. */
export function explain(state: DataState): string {
  switch (state.kind) {
    case 'available':
      return state.dataThrough ? `Observado, dado até ${state.dataThrough}.` : 'Observado.';
    case 'estimated':
      return `Estimado pelo método ${state.method}, confiança ${Math.round(
        state.confidence * 100,
      )}%. Não é valor observado.`;
    case 'unavailable':
      return `Indisponível: ${state.reason}. Não é zero.`;
    case 'not_configured':
      return `Não configurado: falta ${state.missing}. O sistema não inventa um valor.`;
    case 'stale':
      return `Fora do prazo de frescor${
        state.dataThrough ? `, dado até ${state.dataThrough}` : ''
      }. Não pode ser lido como atual.`;
    case 'insufficient_sample':
      return `Amostra insuficiente: ${state.reason}. Não é zero nem infinito.`;
    case 'not_enough_classified_audience':
      // A explicação diz O QUE FAZER, e não só o que falta: classificar mais
      // audiência resolve, esperar não. É a diferença que separa este estado de
      // amostra insuficiente.
      return [
        `Audiência classificada em ${Math.round(state.coverage * 100)}%`,
        state.minimumCoverage === null
          ? 'e o mínimo ainda não foi aprovado'
          : `, abaixo do mínimo de ${Math.round(state.minimumCoverage * 100)}%`,
        state.minimumConfidence === null
          ? '.'
          : ` (confiança mínima por perfil: ${Math.round(state.minimumConfidence * 100)}%).`,
        ' A nota agregada existiria e não significaria nada. Classificar mais audiência resolve; esperar não.',
      ].join('');
    case 'phase_pending':
      return `Entra na ${state.phase}.`;
  }
}
